import { ConfirmationModalBase } from "@/components/common/modals";
import { useUIStore } from "@/workflow_api/store/uiStore";
import { useState } from "react";
import FullModal from "../../full-modal";
import ThreeColumnLayout from "@/components/common/three-column-layout";
import Variables from "@/workflow_api/components/variables";
import Empty from "@/workflow_api/components/variables/empty";
import { ResolutionProvider, useResolution } from "./context";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import {
  NodeTypes,
  type NodeData,
  type RefName,
} from "@/workflow_api/types/workflow";
import { ResolutionSelector } from "./components/ResolutionSelector";
import { ResolutionComment } from "./components/ResolutionComment";
import { ResolutionFiles } from "./components/ResolutionFiles";
import { InputOutput } from "../../InputOutput";
import { useUpdateNode } from "@/workflow_api/services/nodes.api";
import { Icon } from "@elaniintc/goes-design-system";
import type { NodeVariable } from "../types";
import { useGetVariables } from "@/workflow_api/hooks/variables/useGetVariables";
import { useNodeNameValidation } from "@/workflow_api/hooks/useNodeNameValidation";

interface Props {
  initialTitle?: string;
  nodeData?: NodeData;
  flowId: string;
  variables: NodeVariable[];
  isLoading: boolean;
}

const ResolutionDetailsContent = ({
  initialTitle = "Objeto Resolución",
  nodeData,
  flowId,
  isLoading,
  variables,
}: Props) => {
  const [title, setTitle] = useState<string>(initialTitle);
  const [modalState, setModalState] = useState<"idle" | "close-warning">(
    "idle",
  );
  const { getNodeById } = useWorkflowStore();
  const {
    nodeDetailsOpen,
    selectedNodeId,
    setNodeDetailsOpen,
    setSelectedNodeId,
  } = useUIStore();
  const { getNameValidationError, isNameValid } =
    useNodeNameValidation(selectedNodeId);
  const [showNodeTitleValidation, setShowNodeTitleValidation] = useState(() => {
    // Mostrar validación al cargar si el título inicial tiene errores
    return !isNameValid(initialTitle);
  });
  const [manualConfirmation, setManualConfirmation] = useState(() => {
    return nodeData?.manual_continue ?? false;
  });
  const { isValid, statusValid, getInputs, reset } = useResolution();
  const { mutateAsync: updateNode, isPending: isLoadingSave } =
    useUpdateNode(flowId);

  const saveNodeDetails = async () => {
    if (!selectedNodeId) return;
    if (!nodeData) return;
    const selectedNode = getNodeById(selectedNodeId || "");
    if (!selectedNode) return;

    // Obtener inputs
    const inputs = getInputs();
    try {
      await updateNode({
        node_id: selectedNodeId,
        node: {
          flow_id: flowId,
          ref_name: nodeData.type as RefName,
          display_name: title,
          order: nodeData.order,
          position_x: selectedNode.position.x,
          position_y: selectedNode.position.y,
          type: nodeData.node_type,
          manual_continue: manualConfirmation,
          inputs: inputs,
        },
      });
      setNodeDetailsOpen(false);
      reset();
      setSelectedNodeId(undefined);
    } catch (error) {
      console.error("Error updating node:", error);
    }
  };

  return (
    <>
      <ConfirmationModalBase
        isOpen={modalState === "close-warning"}
        acceptText="Salir sin guardar"
        cancelText="Cancelar"
        description="¿Estás seguro de que deseas salir? Todos los cambios que hayas realizado se perderán"
        title="Salir sin guardar"
        acceptVariant="danger-secondary"
        isLoading={false}
        onAccept={() => {
          setNodeDetailsOpen(false);
          setSelectedNodeId();
          setModalState("idle");
        }}
        onClose={() => {
          setModalState("idle");
        }}
      />
      <FullModal
        isOpen={nodeDetailsOpen}
        onClose={() => {
          setModalState("close-warning");
        }}
        isSaveValid={isValid && isNameValid(title)}
        isLoading={isLoadingSave}
        onSave={() => {
          setShowNodeTitleValidation(true);
          if (!isNameValid(title)) return;
          saveNodeDetails();
        }}
      >
        <ThreeColumnLayout
          leftHeader={{
            icon: "arrow-right-to-bracket",
            title: "Inputs",
            color: "neutral",
          }}
          centerHeader={{
            icon: "files",
            title: title,
            color: "action",
            editable: true,
            onTitleChange: (newTitle: string) => {
              setTitle(newTitle);
              if (newTitle.trim() === "") {
                setShowNodeTitleValidation(true);
              }
            },
            onTitleBlur: () => setShowNodeTitleValidation(true),
            validationError: getNameValidationError(title),
            showValidation: showNodeTitleValidation,
            manualConfirmation,
            onManualConfirmationChange: setManualConfirmation,
          }}
          rightHeader={{
            icon: "arrow-right-from-bracket",
            title: "Outputs",
            color: "neutral",
          }}
          leftContent={
            <div className="bg-neutral-foreground overflow-auto divide-y divide-(--border-neutral-quiet)">
              <Variables sourceNodeId={selectedNodeId!} />
            </div>
          }
          centerContent={
            isLoading ? (
              <div>
                <p className="min-h-12 text-body-loud font-semibold flex justify-center items-center py-0.5 px-3 gap-4 ">
                  <Icon
                    type="far"
                    name="spinner-third"
                    className="animate-spin text-xl text-body"
                  />
                </p>
              </div>
            ) : (
              <div className="flex flex-col px-4 pt-3 pb-6 gap-4 border-b border-neutral-quiet">
                <ResolutionSelector />
                <ResolutionComment variables={variables} />
                <ResolutionFiles
                  variables={variables}
                  disabled={!statusValid}
                />
              </div>
            )
          }
          rightContent={
            isValid ? (
              <InputOutput
                nodeType={NodeTypes.RESOLUTION}
                showSteps={false}
                nodeLabel={title}
                variableName="Resolución"
                variables={variables}
              />
            ) : (
              <Empty />
            )
          }
        />
      </FullModal>
    </>
  );
};

const ResolutionDetails = () => {
  const { getNodeById, flowId } = useWorkflowStore();
  const { selectedNodeId } = useUIStore();

  const selectedNode = getNodeById(selectedNodeId || "");
  const nodeData = selectedNode?.data as unknown as NodeData;
  const { variables, isLoading } = useGetVariables();
  return (
    <ResolutionProvider
      inputs={nodeData?.inputs || []}
      variables={variables ?? []}
    >
      <ResolutionDetailsContent
        nodeData={nodeData}
        initialTitle={nodeData.displayName}
        flowId={flowId}
        variables={variables}
        isLoading={isLoading}
      />
    </ResolutionProvider>
  );
};

export { ResolutionDetails };
